import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ArchiveMenuIdentifiers,
  ArchiveMenuResponse,
  BulkArchiveMenusOptions,
  BulkArchiveMenusResponse,
  BulkCreateDishesOptions,
  BulkCreateDishesResponse,
  BulkCreateMenusOptions,
  BulkCreateMenusResponse,
  BulkCreateSectionsOptions,
  BulkCreateSectionsResponse,
  BulkCreateVariationsOptions,
  BulkCreateVariationsResponse,
  BulkUnarchiveMenusOptions,
  BulkUnarchiveMenusResponse,
  BulkUpdateItemsOptions,
  BulkUpdateItemsResponse,
  BulkUpdateMenusOptions,
  BulkUpdateMenusResponse,
  BulkUpdateSectionsOptions,
  BulkUpdateSectionsResponse,
  CatalogChangedEnvelope,
  CatalogCreatedEnvelope,
  CreateDishIdentifiers,
  CreateDishResponse,
  CreateDraftCatalogResponse,
  CreateMenuResponse,
  CreateSectionIdentifiers,
  CreateSectionResponse,
  CreateVariationResponse,
  GetItemIdentifiers,
  GetItemOptions,
  GetItemResponse,
  GetMenuIdentifiers,
  GetMenuResponse,
  GetSectionIdentifiers,
  GetSectionResponse,
  Item,
  ListCatalogsOptions,
  ListCatalogsResponse,
  ListItemsOptions,
  ListItemsResponse,
  ListMenusOptions,
  ListMenusResponse,
  ListSectionsOptions,
  ListSectionsResponse,
  Menu,
  PublishDraftCatalogResponse,
  Section,
  UnarchiveMenuIdentifiers,
  UnarchiveMenuResponse,
  UpdateItemIdentifiers,
  UpdateItemOptions,
  UpdateItemResponse,
  UpdateMenuIdentifiers,
  UpdateMenuOptions,
  UpdateMenuResponse,
  UpdateSectionIdentifiers,
  UpdateSectionOptions,
  UpdateSectionResponse,
  archiveMenu as universalArchiveMenu,
  bulkArchiveMenus as universalBulkArchiveMenus,
  bulkCreateDishes as universalBulkCreateDishes,
  bulkCreateMenus as universalBulkCreateMenus,
  bulkCreateSections as universalBulkCreateSections,
  bulkCreateVariations as universalBulkCreateVariations,
  bulkUnarchiveMenus as universalBulkUnarchiveMenus,
  bulkUpdateItems as universalBulkUpdateItems,
  bulkUpdateMenus as universalBulkUpdateMenus,
  bulkUpdateSections as universalBulkUpdateSections,
  createDish as universalCreateDish,
  createDraftCatalog as universalCreateDraftCatalog,
  createMenu as universalCreateMenu,
  createSection as universalCreateSection,
  createVariation as universalCreateVariation,
  discardDraftCatalog as universalDiscardDraftCatalog,
  getItem as universalGetItem,
  getMenu as universalGetMenu,
  getSection as universalGetSection,
  listCatalogs as universalListCatalogs,
  listItems as universalListItems,
  listMenus as universalListMenus,
  listSections as universalListSections,
  publishDraftCatalog as universalPublishDraftCatalog,
  unarchiveMenu as universalUnarchiveMenu,
  updateItem as universalUpdateItem,
  updateMenu as universalUpdateMenu,
  updateSection as universalUpdateSection,
} from './restaurants-catalogs-v3-catalog-catalogs.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/restaurants' };

export function listCatalogs(httpClient: HttpClient): ListCatalogsSignature {
  return (options?: ListCatalogsOptions) =>
    universalListCatalogs(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListCatalogsSignature {
  /**
   * Retrieves catalogs, given the provided filtering, sorting and paging.
   * Note that there is a single catalog per location.
   * @deprecated
   */
  (options?: ListCatalogsOptions): Promise<
    NonNullablePaths<
      ListCatalogsResponse,
      `catalogs` | `catalogs.${number}.archived`,
      4
    >
  >;
}

export function getMenu(httpClient: HttpClient): GetMenuSignature {
  return (
    identifiers: NonNullablePaths<GetMenuIdentifiers, `catalogId` | `menuId`, 2>
  ) =>
    universalGetMenu(
      identifiers,
      // @ts-ignore
      { httpClient }
    );
}

interface GetMenuSignature {
  /**
   * Retrieves a menu.
   * @deprecated
   */
  (
    identifiers: NonNullablePaths<GetMenuIdentifiers, `catalogId` | `menuId`, 2>
  ): Promise<
    NonNullablePaths<
      GetMenuResponse,
      | `menu.visibilityCriteria.fulfillmentTypes`
      | `menu.visibilityCriteria.platforms`
      | `menu.visibilityCriteria.availability.periods`
      | `menu.visibilityCriteria.availability.periods.${number}.openDay`
      | `menu.visibilityCriteria.availability.periods.${number}.openTime`
      | `menu.visibilityCriteria.availability.periods.${number}.closeDay`
      | `menu.visibilityCriteria.availability.periods.${number}.closeTime`
      | `menu.visibilityCriteria.availability.specialHourPeriods`
      | `menu.visibilityCriteria.availability.specialHourPeriods.${number}.startDate`
      | `menu.visibilityCriteria.availability.specialHourPeriods.${number}.endDate`
      | `menu.visibilityCriteria.availability.specialHourPeriods.${number}.available`
      | `menu.sectionIds.values`,
      7
    >
  >;
}

export function getSection(httpClient: HttpClient): GetSectionSignature {
  return (
    identifiers: NonNullablePaths<
      GetSectionIdentifiers,
      `catalogId` | `menuId` | `sectionId`,
      2
    >
  ) =>
    universalGetSection(
      identifiers,
      // @ts-ignore
      { httpClient }
    );
}

interface GetSectionSignature {
  /**
   * Retrieves a section.
   * @deprecated
   */
  (
    identifiers: NonNullablePaths<
      GetSectionIdentifiers,
      `catalogId` | `menuId` | `sectionId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      GetSectionResponse,
      | `section.itemIds.values`
      | `section.visibilityCriteria.fulfillmentTypes`
      | `section.visibilityCriteria.platforms`
      | `section.visibilityCriteria.availability.periods`
      | `section.visibilityCriteria.availability.periods.${number}.openDay`
      | `section.visibilityCriteria.availability.periods.${number}.openTime`
      | `section.visibilityCriteria.availability.periods.${number}.closeDay`
      | `section.visibilityCriteria.availability.periods.${number}.closeTime`
      | `section.visibilityCriteria.availability.specialHourPeriods`
      | `section.visibilityCriteria.availability.specialHourPeriods.${number}.startDate`
      | `section.visibilityCriteria.availability.specialHourPeriods.${number}.endDate`
      | `section.visibilityCriteria.availability.specialHourPeriods.${number}.available`,
      7
    >
  >;
}

export function updateMenu(httpClient: HttpClient): UpdateMenuSignature {
  return (
    identifiers: NonNullablePaths<UpdateMenuIdentifiers, `catalogId`, 2>,
    options: NonNullablePaths<UpdateMenuOptions, `menu`, 2>
  ) =>
    universalUpdateMenu(
      identifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateMenuSignature {
  /**
   * Updates a menu.
   * You can't update archived menus.
   * @deprecated
   */
  (
    identifiers: NonNullablePaths<UpdateMenuIdentifiers, `catalogId`, 2>,
    options: NonNullablePaths<UpdateMenuOptions, `menu`, 2>
  ): Promise<
    NonNullablePaths<
      UpdateMenuResponse,
      | `menu.visibilityCriteria.fulfillmentTypes`
      | `menu.visibilityCriteria.platforms`
      | `menu.visibilityCriteria.availability.periods`
      | `menu.visibilityCriteria.availability.periods.${number}.openDay`
      | `menu.visibilityCriteria.availability.periods.${number}.openTime`
      | `menu.visibilityCriteria.availability.periods.${number}.closeDay`
      | `menu.visibilityCriteria.availability.periods.${number}.closeTime`
      | `menu.visibilityCriteria.availability.specialHourPeriods`
      | `menu.visibilityCriteria.availability.specialHourPeriods.${number}.startDate`
      | `menu.visibilityCriteria.availability.specialHourPeriods.${number}.endDate`
      | `menu.visibilityCriteria.availability.specialHourPeriods.${number}.available`
      | `menu.sectionIds.values`,
      7
    >
  >;
}

export function updateSection(httpClient: HttpClient): UpdateSectionSignature {
  return (
    identifiers: NonNullablePaths<
      UpdateSectionIdentifiers,
      `catalogId` | `menuId`,
      2
    >,
    options: NonNullablePaths<UpdateSectionOptions, `section`, 2>
  ) =>
    universalUpdateSection(
      identifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateSectionSignature {
  /**
   * Updates a section.
   * You can't update archived sections. Note that every section belongs to a single menu, you can't add it to another menu.
   * @deprecated
   */
  (
    identifiers: NonNullablePaths<
      UpdateSectionIdentifiers,
      `catalogId` | `menuId`,
      2
    >,
    options: NonNullablePaths<UpdateSectionOptions, `section`, 2>
  ): Promise<
    NonNullablePaths<
      UpdateSectionResponse,
      | `section.itemIds.values`
      | `section.visibilityCriteria.fulfillmentTypes`
      | `section.visibilityCriteria.platforms`
      | `section.visibilityCriteria.availability.periods`
      | `section.visibilityCriteria.availability.periods.${number}.openDay`
      | `section.visibilityCriteria.availability.periods.${number}.openTime`
      | `section.visibilityCriteria.availability.periods.${number}.closeDay`
      | `section.visibilityCriteria.availability.periods.${number}.closeTime`
      | `section.visibilityCriteria.availability.specialHourPeriods`
      | `section.visibilityCriteria.availability.specialHourPeriods.${number}.startDate`
      | `section.visibilityCriteria.availability.specialHourPeriods.${number}.endDate`
      | `section.visibilityCriteria.availability.specialHourPeriods.${number}.available`,
      7
    >
  >;
}

export function listMenus(httpClient: HttpClient): ListMenusSignature {
  return (catalogId: string, options?: ListMenusOptions) =>
    universalListMenus(
      catalogId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListMenusSignature {
  /**
   * Retrieves menus, given the provided filtering, sorting and paging.
   * @param - ID of the catalog the menus belong to.
   * @deprecated
   */
  (catalogId: string, options?: ListMenusOptions): Promise<
    NonNullablePaths<ListMenusResponse, `menus`, 2>
  >;
}

export function listSections(httpClient: HttpClient): ListSectionsSignature {
  return (catalogId: string, options?: ListSectionsOptions) =>
    universalListSections(
      catalogId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListSectionsSignature {
  /**
   * Retrieves sections, given the provided filtering, sorting and paging.
   * @param - ID of the catalog the sections belong to.
   * @deprecated
   */
  (catalogId: string, options?: ListSectionsOptions): Promise<
    NonNullablePaths<ListSectionsResponse, `sections`, 2>
  >;
}

export function createMenu(httpClient: HttpClient): CreateMenuSignature {
  return (catalogId: string, menu: Menu) =>
    universalCreateMenu(
      catalogId,
      menu,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateMenuSignature {
  /**
   * Creates a menu.
   * @param - ID of the catalog the menu will belong to.
   * @param - Menu to create.
   * @deprecated
   */
  (catalogId: string, menu: Menu): Promise<
    NonNullablePaths<
      CreateMenuResponse,
      | `menu.visibilityCriteria.fulfillmentTypes`
      | `menu.visibilityCriteria.platforms`
      | `menu.visibilityCriteria.availability.periods`
      | `menu.visibilityCriteria.availability.periods.${number}.openDay`
      | `menu.visibilityCriteria.availability.periods.${number}.openTime`
      | `menu.visibilityCriteria.availability.periods.${number}.closeDay`
      | `menu.visibilityCriteria.availability.periods.${number}.closeTime`
      | `menu.visibilityCriteria.availability.specialHourPeriods`
      | `menu.visibilityCriteria.availability.specialHourPeriods.${number}.startDate`
      | `menu.visibilityCriteria.availability.specialHourPeriods.${number}.endDate`
      | `menu.visibilityCriteria.availability.specialHourPeriods.${number}.available`
      | `menu.sectionIds.values`,
      7
    >
  >;
}

export function archiveMenu(httpClient: HttpClient): ArchiveMenuSignature {
  return (
    identifiers: NonNullablePaths<
      ArchiveMenuIdentifiers,
      `catalogId` | `menuId`,
      2
    >
  ) =>
    universalArchiveMenu(
      identifiers,
      // @ts-ignore
      { httpClient }
    );
}

interface ArchiveMenuSignature {
  /**
   * Archives a menu. You can't update archived menus.
   * @deprecated
   */
  (
    identifiers: NonNullablePaths<
      ArchiveMenuIdentifiers,
      `catalogId` | `menuId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      ArchiveMenuResponse,
      | `menu.visibilityCriteria.fulfillmentTypes`
      | `menu.visibilityCriteria.platforms`
      | `menu.visibilityCriteria.availability.periods`
      | `menu.visibilityCriteria.availability.periods.${number}.openDay`
      | `menu.visibilityCriteria.availability.periods.${number}.openTime`
      | `menu.visibilityCriteria.availability.periods.${number}.closeDay`
      | `menu.visibilityCriteria.availability.periods.${number}.closeTime`
      | `menu.visibilityCriteria.availability.specialHourPeriods`
      | `menu.visibilityCriteria.availability.specialHourPeriods.${number}.startDate`
      | `menu.visibilityCriteria.availability.specialHourPeriods.${number}.endDate`
      | `menu.visibilityCriteria.availability.specialHourPeriods.${number}.available`
      | `menu.sectionIds.values`,
      7
    >
  >;
}

export function unarchiveMenu(httpClient: HttpClient): UnarchiveMenuSignature {
  return (
    identifiers: NonNullablePaths<
      UnarchiveMenuIdentifiers,
      `catalogId` | `menuId`,
      2
    >
  ) =>
    universalUnarchiveMenu(
      identifiers,
      // @ts-ignore
      { httpClient }
    );
}

interface UnarchiveMenuSignature {
  /**
   * Unarchives a menu.
   * @deprecated
   */
  (
    identifiers: NonNullablePaths<
      UnarchiveMenuIdentifiers,
      `catalogId` | `menuId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      UnarchiveMenuResponse,
      | `menu.visibilityCriteria.fulfillmentTypes`
      | `menu.visibilityCriteria.platforms`
      | `menu.visibilityCriteria.availability.periods`
      | `menu.visibilityCriteria.availability.periods.${number}.openDay`
      | `menu.visibilityCriteria.availability.periods.${number}.openTime`
      | `menu.visibilityCriteria.availability.periods.${number}.closeDay`
      | `menu.visibilityCriteria.availability.periods.${number}.closeTime`
      | `menu.visibilityCriteria.availability.specialHourPeriods`
      | `menu.visibilityCriteria.availability.specialHourPeriods.${number}.startDate`
      | `menu.visibilityCriteria.availability.specialHourPeriods.${number}.endDate`
      | `menu.visibilityCriteria.availability.specialHourPeriods.${number}.available`
      | `menu.sectionIds.values`,
      7
    >
  >;
}

export function createSection(httpClient: HttpClient): CreateSectionSignature {
  return (
    identifiers: NonNullablePaths<
      CreateSectionIdentifiers,
      `catalogId` | `menuId`,
      2
    >,
    section: Section
  ) =>
    universalCreateSection(
      identifiers,
      section,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateSectionSignature {
  /**
   * Creates a section.
   * You can't add the section to multiple menus, since every section belongs to a single menu.
   * @param - Section to create.
   * @deprecated
   */
  (
    identifiers: NonNullablePaths<
      CreateSectionIdentifiers,
      `catalogId` | `menuId`,
      2
    >,
    section: Section
  ): Promise<
    NonNullablePaths<
      CreateSectionResponse,
      | `section.itemIds.values`
      | `section.visibilityCriteria.fulfillmentTypes`
      | `section.visibilityCriteria.platforms`
      | `section.visibilityCriteria.availability.periods`
      | `section.visibilityCriteria.availability.periods.${number}.openDay`
      | `section.visibilityCriteria.availability.periods.${number}.openTime`
      | `section.visibilityCriteria.availability.periods.${number}.closeDay`
      | `section.visibilityCriteria.availability.periods.${number}.closeTime`
      | `section.visibilityCriteria.availability.specialHourPeriods`
      | `section.visibilityCriteria.availability.specialHourPeriods.${number}.startDate`
      | `section.visibilityCriteria.availability.specialHourPeriods.${number}.endDate`
      | `section.visibilityCriteria.availability.specialHourPeriods.${number}.available`,
      7
    >
  >;
}

export function getItem(httpClient: HttpClient): GetItemSignature {
  return (
    identifiers: NonNullablePaths<
      GetItemIdentifiers,
      `catalogId` | `itemId`,
      2
    >,
    options?: GetItemOptions
  ) =>
    universalGetItem(
      identifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetItemSignature {
  /**
   * Retrieves an item.
   * @deprecated
   */
  (
    identifiers: NonNullablePaths<
      GetItemIdentifiers,
      `catalogId` | `itemId`,
      2
    >,
    options?: GetItemOptions
  ): Promise<
    NonNullablePaths<
      GetItemResponse,
      | `item.price.value`
      | `item.price.currency`
      | `item.labels.values`
      | `item.visibilityCriteria.fulfillmentTypes`
      | `item.visibilityCriteria.platforms`
      | `item.visibilityCriteria.availability.periods`
      | `item.visibilityCriteria.availability.periods.${number}.openDay`
      | `item.visibilityCriteria.availability.periods.${number}.openTime`
      | `item.visibilityCriteria.availability.periods.${number}.closeDay`
      | `item.visibilityCriteria.availability.periods.${number}.closeTime`
      | `item.visibilityCriteria.availability.specialHourPeriods`
      | `item.visibilityCriteria.availability.specialHourPeriods.${number}.startDate`
      | `item.visibilityCriteria.availability.specialHourPeriods.${number}.endDate`
      | `item.visibilityCriteria.availability.specialHourPeriods.${number}.available`
      | `item.dishOptions.values`
      | `item.dishOptions.values.${number}.name`
      | `item.dishOptions.values.${number}.type`
      | `item.type`,
      7
    >
  >;
}

export function listItems(httpClient: HttpClient): ListItemsSignature {
  return (catalogId: string, options?: ListItemsOptions) =>
    universalListItems(
      catalogId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListItemsSignature {
  /**
   * Retrieves items, given the provided filtering, sorting and paging.
   * @param - ID of the catalog the items belong to.
   * @deprecated
   */
  (catalogId: string, options?: ListItemsOptions): Promise<
    NonNullablePaths<
      ListItemsResponse,
      | `items`
      | `items.${number}.price.value`
      | `items.${number}.price.currency`
      | `items.${number}.type`,
      5
    >
  >;
}

export function updateItem(httpClient: HttpClient): UpdateItemSignature {
  return (
    identifiers: NonNullablePaths<UpdateItemIdentifiers, `catalogId`, 2>,
    options: NonNullablePaths<UpdateItemOptions, `item`, 2>
  ) =>
    universalUpdateItem(
      identifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateItemSignature {
  /**
   * Updates an item.
   * You can't update archived items.
   * @deprecated
   */
  (
    identifiers: NonNullablePaths<UpdateItemIdentifiers, `catalogId`, 2>,
    options: NonNullablePaths<UpdateItemOptions, `item`, 2>
  ): Promise<
    NonNullablePaths<
      UpdateItemResponse,
      | `item.price.value`
      | `item.price.currency`
      | `item.labels.values`
      | `item.visibilityCriteria.fulfillmentTypes`
      | `item.visibilityCriteria.platforms`
      | `item.visibilityCriteria.availability.periods`
      | `item.visibilityCriteria.availability.periods.${number}.openDay`
      | `item.visibilityCriteria.availability.periods.${number}.openTime`
      | `item.visibilityCriteria.availability.periods.${number}.closeDay`
      | `item.visibilityCriteria.availability.periods.${number}.closeTime`
      | `item.visibilityCriteria.availability.specialHourPeriods`
      | `item.visibilityCriteria.availability.specialHourPeriods.${number}.startDate`
      | `item.visibilityCriteria.availability.specialHourPeriods.${number}.endDate`
      | `item.visibilityCriteria.availability.specialHourPeriods.${number}.available`
      | `item.dishOptions.values`
      | `item.dishOptions.values.${number}.name`
      | `item.dishOptions.values.${number}.type`
      | `item.type`,
      7
    >
  >;
}

export function createDish(httpClient: HttpClient): CreateDishSignature {
  return (
    identifiers: NonNullablePaths<
      CreateDishIdentifiers,
      `catalogId` | `menuId` | `sectionId`,
      2
    >,
    dish: Item
  ) =>
    universalCreateDish(
      identifiers,
      dish,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateDishSignature {
  /**
   * Creates a dish.
   * @param - Item of type `dish` to create.
   * @deprecated
   */
  (
    identifiers: NonNullablePaths<
      CreateDishIdentifiers,
      `catalogId` | `menuId` | `sectionId`,
      2
    >,
    dish: Item
  ): Promise<
    NonNullablePaths<
      CreateDishResponse,
      | `dish.price.value`
      | `dish.price.currency`
      | `dish.labels.values`
      | `dish.visibilityCriteria.fulfillmentTypes`
      | `dish.visibilityCriteria.platforms`
      | `dish.visibilityCriteria.availability.periods`
      | `dish.visibilityCriteria.availability.periods.${number}.openDay`
      | `dish.visibilityCriteria.availability.periods.${number}.openTime`
      | `dish.visibilityCriteria.availability.periods.${number}.closeDay`
      | `dish.visibilityCriteria.availability.periods.${number}.closeTime`
      | `dish.visibilityCriteria.availability.specialHourPeriods`
      | `dish.visibilityCriteria.availability.specialHourPeriods.${number}.startDate`
      | `dish.visibilityCriteria.availability.specialHourPeriods.${number}.endDate`
      | `dish.visibilityCriteria.availability.specialHourPeriods.${number}.available`
      | `dish.dishOptions.values`
      | `dish.dishOptions.values.${number}.name`
      | `dish.dishOptions.values.${number}.type`
      | `dish.type`,
      7
    >
  >;
}

export function createVariation(
  httpClient: HttpClient
): CreateVariationSignature {
  return (catalogId: string, name: string) =>
    universalCreateVariation(
      catalogId,
      name,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateVariationSignature {
  /**
   * Creates a variation.
   * @param - ID of the catalog the variation will belong to.
   * @param - Variation name.
   * @deprecated
   */
  (catalogId: string, name: string): Promise<
    NonNullablePaths<
      CreateVariationResponse,
      | `variation.price.value`
      | `variation.price.currency`
      | `variation.labels.values`
      | `variation.visibilityCriteria.fulfillmentTypes`
      | `variation.visibilityCriteria.platforms`
      | `variation.visibilityCriteria.availability.periods`
      | `variation.visibilityCriteria.availability.periods.${number}.openDay`
      | `variation.visibilityCriteria.availability.periods.${number}.openTime`
      | `variation.visibilityCriteria.availability.periods.${number}.closeDay`
      | `variation.visibilityCriteria.availability.periods.${number}.closeTime`
      | `variation.visibilityCriteria.availability.specialHourPeriods`
      | `variation.visibilityCriteria.availability.specialHourPeriods.${number}.startDate`
      | `variation.visibilityCriteria.availability.specialHourPeriods.${number}.endDate`
      | `variation.visibilityCriteria.availability.specialHourPeriods.${number}.available`
      | `variation.dishOptions.values`
      | `variation.dishOptions.values.${number}.name`
      | `variation.dishOptions.values.${number}.type`
      | `variation.type`,
      7
    >
  >;
}

export function createDraftCatalog(
  httpClient: HttpClient
): CreateDraftCatalogSignature {
  return (catalogId: string) =>
    universalCreateDraftCatalog(
      catalogId,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateDraftCatalogSignature {
  /**
   * Creates a draft version of the specified catalog.
   * Then you can update and publish the draft catalog by using the endpoints in the [Draft Catalogs category](https://dev.wix.com/api/rest/wix-restaurants/catalogs/draft-catalogs).
   *
   * > **Note:** You can't create more than one draft catalog per location.
   * @param - ID of the catalog to create a draft version for.
   * @deprecated
   */
  (catalogId: string): Promise<
    NonNullablePaths<CreateDraftCatalogResponse, `catalog.archived`, 3>
  >;
}

export function publishDraftCatalog(
  httpClient: HttpClient
): PublishDraftCatalogSignature {
  return (catalogId: string) =>
    universalPublishDraftCatalog(
      catalogId,
      // @ts-ignore
      { httpClient }
    );
}

interface PublishDraftCatalogSignature {
  /**
   * Publishes a draft catalog to the live site by replacing the existing catalog.
   * @param - ID of the draft catalog to publish.
   * @deprecated
   */
  (catalogId: string): Promise<
    NonNullablePaths<PublishDraftCatalogResponse, `catalog.archived`, 3>
  >;
}

export function discardDraftCatalog(
  httpClient: HttpClient
): DiscardDraftCatalogSignature {
  return (catalogId: string) =>
    universalDiscardDraftCatalog(
      catalogId,
      // @ts-ignore
      { httpClient }
    );
}

interface DiscardDraftCatalogSignature {
  /**
   * Discards a draft catalog. If you want to continue using [Bulk endpoints](https://dev.wix.com/api/rest/wix-restaurants/catalogs/draft-catalogs) for this catalog, you must first call the [Create Draft Catalog](https://dev.wix.com/api/rest/wix-restaurants/catalogs/draft-catalogs/create-draft-catalog) endpoint again.
   * @param - ID of the draft catalog to discard.
   * @deprecated
   */
  (catalogId: string): Promise<void>;
}

export function bulkCreateVariations(
  httpClient: HttpClient
): BulkCreateVariationsSignature {
  return (catalogId: string, options?: BulkCreateVariationsOptions) =>
    universalBulkCreateVariations(
      catalogId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateVariationsSignature {
  /**
   * Adds multiple variations to a draft catalog.
   * @param - ID of the catalog the variations will belong to.
   * @deprecated
   */
  (catalogId: string, options?: BulkCreateVariationsOptions): Promise<
    NonNullablePaths<
      BulkCreateVariationsResponse,
      | `results`
      | `results.${number}.entityMetadata.originalIndex`
      | `results.${number}.entityMetadata.success`
      | `results.${number}.entityMetadata.error.code`
      | `results.${number}.entityMetadata.error.description`
      | `results.${number}.entity.price.value`
      | `results.${number}.entity.price.currency`
      | `results.${number}.entity.type`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateItems(
  httpClient: HttpClient
): BulkUpdateItemsSignature {
  return (catalogId: string, options?: BulkUpdateItemsOptions) =>
    universalBulkUpdateItems(
      catalogId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateItemsSignature {
  /**
   * Updates multiple items in a draft catalog.
   * You can't update archived items.
   * @param - ID of the catalog the items belong to.
   * @deprecated
   */
  (catalogId: string, options?: BulkUpdateItemsOptions): Promise<
    NonNullablePaths<
      BulkUpdateItemsResponse,
      | `results`
      | `results.${number}.entityMetadata.originalIndex`
      | `results.${number}.entityMetadata.success`
      | `results.${number}.entityMetadata.error.code`
      | `results.${number}.entityMetadata.error.description`
      | `results.${number}.entity.price.value`
      | `results.${number}.entity.price.currency`
      | `results.${number}.entity.type`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkCreateDishes(
  httpClient: HttpClient
): BulkCreateDishesSignature {
  return (catalogId: string, options?: BulkCreateDishesOptions) =>
    universalBulkCreateDishes(
      catalogId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateDishesSignature {
  /**
   * Adds multiple dishes to a draft catalog.
   * @param - ID of the catalog the dishes will belong to.
   * @deprecated
   */
  (catalogId: string, options?: BulkCreateDishesOptions): Promise<
    NonNullablePaths<
      BulkCreateDishesResponse,
      | `results`
      | `results.${number}.entityMetadata.originalIndex`
      | `results.${number}.entityMetadata.success`
      | `results.${number}.entityMetadata.error.code`
      | `results.${number}.entityMetadata.error.description`
      | `results.${number}.entity.price.value`
      | `results.${number}.entity.price.currency`
      | `results.${number}.entity.type`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkCreateMenus(
  httpClient: HttpClient
): BulkCreateMenusSignature {
  return (catalogId: string, options?: BulkCreateMenusOptions) =>
    universalBulkCreateMenus(
      catalogId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateMenusSignature {
  /**
   * Adds multiple menus to a draft catalog.
   * @param - ID of the catalog the menus will belong to.
   * @deprecated
   */
  (catalogId: string, options?: BulkCreateMenusOptions): Promise<
    NonNullablePaths<
      BulkCreateMenusResponse,
      | `results`
      | `results.${number}.entityMetadata.originalIndex`
      | `results.${number}.entityMetadata.success`
      | `results.${number}.entityMetadata.error.code`
      | `results.${number}.entityMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateMenus(
  httpClient: HttpClient
): BulkUpdateMenusSignature {
  return (catalogId: string, options?: BulkUpdateMenusOptions) =>
    universalBulkUpdateMenus(
      catalogId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateMenusSignature {
  /**
   * Updates multiple menus in a draft catalog.
   * You can't update archived menus.
   * @param - ID of the catalog the menus belong to.
   * @deprecated
   */
  (catalogId: string, options?: BulkUpdateMenusOptions): Promise<
    NonNullablePaths<
      BulkUpdateMenusResponse,
      | `results`
      | `results.${number}.entityMetadata.originalIndex`
      | `results.${number}.entityMetadata.success`
      | `results.${number}.entityMetadata.error.code`
      | `results.${number}.entityMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkCreateSections(
  httpClient: HttpClient
): BulkCreateSectionsSignature {
  return (catalogId: string, options?: BulkCreateSectionsOptions) =>
    universalBulkCreateSections(
      catalogId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateSectionsSignature {
  /**
   * Adds multiple sections to a draft catalog.
   * @param - ID of the catalog the sections will belong to.
   * @deprecated
   */
  (catalogId: string, options?: BulkCreateSectionsOptions): Promise<
    NonNullablePaths<
      BulkCreateSectionsResponse,
      | `results`
      | `results.${number}.entityMetadata.originalIndex`
      | `results.${number}.entityMetadata.success`
      | `results.${number}.entityMetadata.error.code`
      | `results.${number}.entityMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateSections(
  httpClient: HttpClient
): BulkUpdateSectionsSignature {
  return (catalogId: string, options?: BulkUpdateSectionsOptions) =>
    universalBulkUpdateSections(
      catalogId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateSectionsSignature {
  /**
   * Updates multiple sections in a draft catalog.
   * You can't update archived sections.
   * @param - ID of the catalog the sections belong to.
   * @deprecated
   */
  (catalogId: string, options?: BulkUpdateSectionsOptions): Promise<
    NonNullablePaths<
      BulkUpdateSectionsResponse,
      | `results`
      | `results.${number}.entityMetadata.originalIndex`
      | `results.${number}.entityMetadata.success`
      | `results.${number}.entityMetadata.error.code`
      | `results.${number}.entityMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkArchiveMenus(
  httpClient: HttpClient
): BulkArchiveMenusSignature {
  return (catalogId: string, options?: BulkArchiveMenusOptions) =>
    universalBulkArchiveMenus(
      catalogId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkArchiveMenusSignature {
  /**
   * Changes the `archived` boolean to `true` for multiple menus in a draft catalog.
   * @param - ID of the catalog the menus belong to.
   * @deprecated
   */
  (catalogId: string, options?: BulkArchiveMenusOptions): Promise<
    NonNullablePaths<
      BulkArchiveMenusResponse,
      | `results`
      | `results.${number}.entityMetadata.originalIndex`
      | `results.${number}.entityMetadata.success`
      | `results.${number}.entityMetadata.error.code`
      | `results.${number}.entityMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUnarchiveMenus(
  httpClient: HttpClient
): BulkUnarchiveMenusSignature {
  return (catalogId: string, options?: BulkUnarchiveMenusOptions) =>
    universalBulkUnarchiveMenus(
      catalogId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUnarchiveMenusSignature {
  /**
   * Changes the `archived` boolean to `false` for multiple menus in a draft catalog.
   * @param - ID of the catalog the menus belong to.
   * @deprecated
   */
  (catalogId: string, options?: BulkUnarchiveMenusOptions): Promise<
    NonNullablePaths<
      BulkUnarchiveMenusResponse,
      | `results`
      | `results.${number}.entityMetadata.originalIndex`
      | `results.${number}.entityMetadata.success`
      | `results.${number}.entityMetadata.error.code`
      | `results.${number}.entityMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export const onCatalogChanged = EventDefinition(
  'wix.restaurants.catalogs.v3.catalog_catalog_changed',
  true,
  (event: CatalogChangedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.catalog.draftPublishedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CatalogChangedEnvelope>();
export const onCatalogCreated = EventDefinition(
  'wix.restaurants.catalogs.v3.catalog_created',
  true,
  (event: CatalogCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.draftPublishedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CatalogCreatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  App,
  ApplicationError,
  ArchiveMenuIdentifiers,
  ArchiveMenuRequest,
  ArchiveMenuResponse,
  ArchiveSectionRequest,
  ArchiveSectionResponse,
  Availability,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkArchiveMenusOptions,
  BulkArchiveMenusRequest,
  BulkArchiveMenusResponse,
  BulkCreateDishesOptions,
  BulkCreateDishesRequest,
  BulkCreateDishesResponse,
  BulkCreateMenusOptions,
  BulkCreateMenusRequest,
  BulkCreateMenusResponse,
  BulkCreateSectionsOptions,
  BulkCreateSectionsRequest,
  BulkCreateSectionsResponse,
  BulkCreateVariationsOptions,
  BulkCreateVariationsRequest,
  BulkCreateVariationsResponse,
  BulkItemResult,
  BulkMenuResult,
  BulkSectionResult,
  BulkUnarchiveMenusOptions,
  BulkUnarchiveMenusRequest,
  BulkUnarchiveMenusResponse,
  BulkUpdateItemsOptions,
  BulkUpdateItemsRequest,
  BulkUpdateItemsResponse,
  BulkUpdateMenusOptions,
  BulkUpdateMenusRequest,
  BulkUpdateMenusResponse,
  BulkUpdateSectionsOptions,
  BulkUpdateSectionsRequest,
  BulkUpdateSectionsResponse,
  Catalog,
  CatalogChanged,
  CatalogChangedEnvelope,
  CatalogCreatedEnvelope,
  CatalogUpdated,
  Coupon,
  CreateDishIdentifiers,
  CreateDishRequest,
  CreateDishResponse,
  CreateDraftCatalogRequest,
  CreateDraftCatalogResponse,
  CreateMenuRequest,
  CreateMenuResponse,
  CreateSectionIdentifiers,
  CreateSectionRequest,
  CreateSectionResponse,
  CreateVariationRequest,
  CreateVariationResponse,
  CustomTag,
  DayOfWeek,
  Deselection,
  DiscardDraftCatalogRequest,
  DiscardDraftCatalogResponse,
  Discount,
  DiscountApplyToFilterOneOf,
  DiscountCondition,
  DiscountPlatform,
  DiscountType,
  DiscountValueOneOf,
  DishOption,
  DishOptionItem,
  DishOptionMethodOneOf,
  DishOptions,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  Extras,
  File,
  FulfillmentType,
  GetItemIdentifiers,
  GetItemOptions,
  GetItemRequest,
  GetItemResponse,
  GetMenuIdentifiers,
  GetMenuRequest,
  GetMenuResponse,
  GetSectionIdentifiers,
  GetSectionRequest,
  GetSectionResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  IndexCacheCmd,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  Item,
  ItemIds,
  ItemMetadata,
  ItemType,
  Labels,
  ListCatalogsOptions,
  ListCatalogsRequest,
  ListCatalogsResponse,
  ListItemsOptions,
  ListItemsRequest,
  ListItemsResponse,
  ListMenusOptions,
  ListMenusRequest,
  ListMenusResponse,
  ListSectionsOptions,
  ListSectionsRequest,
  ListSectionsResponse,
  Menu,
  MenuSectionIds,
  MessageEnvelope,
  Money,
  NewVariation,
  Page,
  Pages,
  Paging,
  Platform,
  PublishCatalogUpdated,
  PublishDraftCatalogRequest,
  PublishDraftCatalogResponse,
  RemoveDishFromSectionRequest,
  RemoveDishFromSectionResponse,
  RestoreInfo,
  Section,
  SectionIds,
  SectionItemIds,
  Selection,
  SortOrder,
  Sorting,
  SpecialHourPeriod,
  TimePeriod,
  Type,
  URI,
  URIs,
  UnarchiveMenuIdentifiers,
  UnarchiveMenuRequest,
  UnarchiveMenuResponse,
  UpdateItemIdentifiers,
  UpdateItemOptions,
  UpdateItemRequest,
  UpdateItemResponse,
  UpdateMenuIdentifiers,
  UpdateMenuOptions,
  UpdateMenuRequest,
  UpdateMenuResponse,
  UpdateSectionIdentifiers,
  UpdateSectionOptions,
  UpdateSectionRequest,
  UpdateSectionResponse,
  V3CreateDishRequest,
  V3CreateSectionRequest,
  V3UpdateSectionRequest,
  VisibilityCriteria,
  WebhookIdentityType,
} from './restaurants-catalogs-v3-catalog-catalogs.universal.js';
